import { Link } from "react-router-dom";
import { useAuthStore } from "@/store/auth";
import { useWeeklyReports, useAcceptWeeklyReport, useRejectWeeklyReport, useMarkWeeklyReportRead } from "../hooks/useWeeklyReports";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { PageLoader } from "@/components/ui/Spinner";
import { WEEKLY_REPORT_STATUS_LABELS, type WeeklyReportStatus, type WeeklyReport } from "../types";
import { useState } from "react";
import { CheckCircle2, XCircle, Eye, FileText, AlertCircle } from "lucide-react";

const statusColors: Record<WeeklyReportStatus, "gray" | "blue" | "green" | "red" | "yellow"> = {
  pending: "blue",
  submitted: "yellow",
  late: "red",
  accepted: "green",
  rejected: "red",
};

function formatDate(dateStr: string | null) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("pl-PL", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function RejectDialog({ onConfirm, onCancel, isPending }: { onConfirm: (comment: string) => void; onCancel: () => void; isPending: boolean }) {
  const [comment, setComment] = useState("");
  const [err, setErr] = useState("");
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl p-6 w-full max-w-md">
        <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Odrzuć raport</h3>
        <textarea
          className="w-full border rounded-md px-3 py-2 text-sm min-h-[80px] dark:bg-gray-800 dark:border-gray-600 dark:text-white"
          placeholder="Komentarz (wymagany)"
          value={comment}
          onChange={(e) => { setComment(e.target.value); setErr(""); }}
        />
        {err && <p className="text-xs text-red-500 mt-1">{err}</p>}
        <div className="flex gap-2 mt-4 justify-end">
          <Button variant="ghost" size="sm" onClick={onCancel} disabled={isPending}>Anuluj</Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => {
              if (!comment.trim()) { setErr("Komentarz jest wymagany."); return; }
              onConfirm(comment.trim());
            }}
            disabled={isPending}
          >
            Odrzuć
          </Button>
        </div>
      </div>
    </div>
  );
}

function ReportActions({ report, isGK, orderPath }: { report: WeeklyReport; isGK: boolean; orderPath: string }) {
  const acceptMutation = useAcceptWeeklyReport();
  const rejectMutation = useRejectWeeklyReport();
  const markReadMutation = useMarkWeeklyReportRead();
  const [showReject, setShowReject] = useState(false);

  return (
    <>
      <div className="flex gap-1.5 justify-end">
        {isGK && report.status === "submitted" && (
          <>
            {!report.director_read && (
              <button
                title="Oznacz jako przeczytany"
                className="p-1.5 rounded text-gray-400 hover:text-[var(--color-primary)] hover:bg-[var(--color-primary-dim)] transition-colors"
                onClick={() => markReadMutation.mutate(report.id)}
                disabled={markReadMutation.isPending}
              >
                <Eye size={15} />
              </button>
            )}
            <button
              title="Zaakceptuj raport"
              className="p-1.5 rounded text-gray-400 hover:text-green-600 hover:bg-green-50 transition-colors"
              onClick={() => acceptMutation.mutate(report.id)}
              disabled={acceptMutation.isPending}
            >
              <CheckCircle2 size={15} />
            </button>
            <button
              title="Odrzuć raport"
              className="p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
              onClick={() => setShowReject(true)}
            >
              <XCircle size={15} />
            </button>
          </>
        )}
        <Link
          to={orderPath}
          className="p-1.5 rounded text-gray-400 hover:text-[var(--color-primary)] hover:bg-[var(--color-primary-dim)] transition-colors"
          title="Przejdź do zamówienia"
        >
          <FileText size={15} />
        </Link>
      </div>
      {showReject && (
        <RejectDialog
          onConfirm={(comment) => rejectMutation.mutate({ id: report.id, comment }, { onSuccess: () => setShowReject(false) })}
          onCancel={() => setShowReject(false)}
          isPending={rejectMutation.isPending}
        />
      )}
    </>
  );
}

function ReportCard({ report, isGK }: { report: WeeklyReport; isGK: boolean }) {
  const orderPath = isGK ? `/orders/${report.order}` : `/contractor/orders/${report.order}`;
  const isUnread = isGK && report.status === "submitted" && !report.director_read;

  return (
    <div className={`border border-gray-200 dark:border-gray-700 rounded-lg p-4 ${isUnread ? "bg-blue-50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-700" : "bg-white dark:bg-gray-800"}`}>
      <div className="flex items-start justify-between gap-2 mb-3">
        <Link to={orderPath} className="font-semibold text-[var(--color-primary)] hover:underline text-sm leading-tight">
          {report.order_number}
        </Link>
        <Badge color={statusColors[report.status]}>{WEEKLY_REPORT_STATUS_LABELS[report.status]}</Badge>
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs text-gray-500 dark:text-gray-400 mb-3">
        <div>
          <span className="font-medium text-gray-400 uppercase tracking-wide text-[10px]">Okres</span>
          <p className="text-gray-700 dark:text-gray-300 mt-0.5">{formatDate(report.period_start)} – {formatDate(report.period_end)}</p>
        </div>
        <div>
          <span className="font-medium text-gray-400 uppercase tracking-wide text-[10px]">Termin</span>
          <p className="text-gray-700 dark:text-gray-300 mt-0.5">{formatDate(report.due_date)}</p>
        </div>
        {report.submitted_at && (
          <div>
            <span className="font-medium text-gray-400 uppercase tracking-wide text-[10px]">Złożony</span>
            <p className="text-gray-700 dark:text-gray-300 mt-0.5">{formatDate(report.submitted_at)}</p>
          </div>
        )}
        {report.director_comment && (
          <div className="col-span-2">
            <span className="font-medium text-gray-400 uppercase tracking-wide text-[10px]">Komentarz</span>
            <p className="text-gray-700 dark:text-gray-300 mt-0.5 truncate">{report.director_comment}</p>
          </div>
        )}
      </div>
      <div className="flex justify-end border-t border-gray-100 dark:border-gray-700 pt-2">
        <ReportActions report={report} isGK={isGK} orderPath={orderPath} />
      </div>
    </div>
  );
}

function ReportRow({ report, isGK }: { report: WeeklyReport; isGK: boolean }) {
  const orderPath = isGK ? `/orders/${report.order}` : `/contractor/orders/${report.order}`;
  const isUnread = isGK && report.status === "submitted" && !report.director_read;

  return (
    <tr className={`border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50 border-l-2 border-l-transparent hover:border-l-[var(--color-primary)] transition-colors ${isUnread ? "bg-blue-50 dark:bg-blue-900/10" : ""}`}>
      <td className="px-4 py-3 text-sm">
        <div className="flex items-center gap-2">
          {isUnread && <span className="inline-block w-2 h-2 rounded-full bg-[var(--bg-elevated)]0 flex-shrink-0" />}
          <Link to={orderPath} className="font-medium text-[var(--color-primary)] dark:text-[var(--color-primary)] hover:underline">
            {report.order_number}
          </Link>
        </div>
      </td>
      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
        {formatDate(report.period_start)} – {formatDate(report.period_end)}
      </td>
      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
        {formatDate(report.due_date)}
      </td>
      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
        {report.submitted_at ? formatDate(report.submitted_at) : "—"}
      </td>
      <td className="px-4 py-3">
        <Badge color={statusColors[report.status]}>{WEEKLY_REPORT_STATUS_LABELS[report.status]}</Badge>
      </td>
      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 max-w-[160px] truncate" title={report.director_comment}>
        {report.director_comment || "—"}
      </td>
      <td className="px-4 py-3">
        <ReportActions report={report} isGK={isGK} orderPath={orderPath} />
      </td>
    </tr>
  );
}

export function WeeklyReportsPage() {
  const user = useAuthStore((s) => s.user);
  const isGK = user?.role === "director" || user?.role === "coordinator";
  const isContractor = user?.role === "contractor";

  const [statusFilter, setStatusFilter] = useState<string>("all");
  const { data: reports = [], isLoading } = useWeeklyReports();

  if (!isGK && !isContractor) {
    return (
      <div className="p-8 text-center text-gray-500">
        Brak dostępu do raportów tygodniowych.
      </div>
    );
  }

  const filtered = statusFilter === "all"
    ? reports
    : reports.filter((r) => r.status === statusFilter);

  const pendingLate = reports.filter((r) => r.status === "pending" || r.status === "late");
  const unreadSubmitted = reports.filter((r) => r.status === "submitted" && !r.director_read);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Raporty tygodniowe</h1>
            <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1" />
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            {isGK ? "Raporty ze wszystkich zamówień" : "Twoje raporty do złożenia i historia"}
          </p>
        </div>
      </div>

      {/* Summary cards */}
      {isContractor && pendingLate.length > 0 && (
        <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg flex items-start gap-3">
          <AlertCircle size={18} className="text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
              Masz {pendingLate.length} raport{pendingLate.length === 1 ? "" : pendingLate.length < 5 ? "y" : "ów"} do złożenia
            </p>
            <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
              Przejdź do zamówienia, aby wypełnić i złożyć raport.
            </p>
          </div>
        </div>
      )}
      {isGK && unreadSubmitted.length > 0 && (
        <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg flex items-start gap-3">
          <AlertCircle size={18} className="text-[var(--color-primary)] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-[var(--color-primary)] dark:text-blue-300">
              {unreadSubmitted.length} nowych raport{unreadSubmitted.length === 1 ? "" : unreadSubmitted.length < 5 ? "y" : "ów"} do przeglądu
            </p>
            <p className="text-xs text-[var(--color-primary)] dark:text-blue-400 mt-0.5">
              Zaakceptuj lub odrzuć poniżej.
            </p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-2 mb-4 flex-wrap">
        {(["all", "pending", "late", "submitted", "accepted", "rejected"] as const).map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              statusFilter === s
                ? "bg-[var(--color-primary)] text-white"
                : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
            }`}
          >
            {s === "all" ? "Wszystkie" : WEEKLY_REPORT_STATUS_LABELS[s as WeeklyReportStatus]}
            {s !== "all" && (
              <span className="ml-1 opacity-70">
                ({reports.filter((r) => r.status === s).length})
              </span>
            )}
          </button>
        ))}
      </div>

      {isLoading ? (
        <PageLoader />
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-400 dark:text-gray-500">
          <FileText size={40} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm">Brak raportów w wybranym filtrze.</p>
        </div>
      ) : (
        <>
          {/* Mobile: card list */}
          <div className="sm:hidden flex flex-col gap-3">
            {filtered.map((report) => (
              <ReportCard key={report.id} report={report} isGK={isGK} />
            ))}
          </div>

          {/* Desktop: table */}
          <div className="hidden sm:block bg-white dark:bg-gray-900 overflow-hidden">
            <div className="bg-white dark:bg-gray-800 overflow-x-auto">
              <table className="w-full text-left text-xs min-w-[640px]">
                <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                  <tr>
                    <th className="px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Zamówienie</th>
                    <th className="px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Okres</th>
                    <th className="px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Termin</th>
                    <th className="px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Złożony</th>
                    <th className="px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Status</th>
                    <th className="px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Komentarz</th>
                    <th className="px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400" />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((report) => (
                    <ReportRow key={report.id} report={report} isGK={isGK} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
